/**
 * JSON ingestion: arrays of objects, a single object, or newline-delimited
 * JSON (NDJSON). Nested objects flatten to dot-paths; arrays serialize to
 * compact JSON strings so every cell stays a scalar for the grid.
 */

export interface JsonTable {
  header: string[];
  rows: string[][];
  /** True when at least one record nested beyond depth 1. */
  flattened: boolean;
}

/** Flatten a record into dot-path keys, preserving key insertion order. */
export function flattenRecord(record: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (value: unknown, path: string) => {
    if (value === null || value === undefined) {
      if (path) out[path] = cellToText(null);
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        out[path] = '[]';
        return;
      }
      out[path] = JSON.stringify(value);
      return;
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        walk(v, path ? `${path}.${k}` : k);
      }
      return;
    }
    out[path] = cellToText(value);
  };
  if (typeof record !== 'object' || record === null || Array.isArray(record)) {
    walk(record, '');
  } else {
    walk(record, '');
  }
  return out;
}

/** Convert a JSON scalar to the string domain used everywhere downstream. */
export function cellToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value) ?? '';
}

function isRecordArray(input: unknown): input is Record<string, unknown>[] {
  return (
    Array.isArray(input) &&
    input.every((v) => typeof v === 'object' && v !== null && !Array.isArray(v))
  );
}

/**
 * Parse JSON text (array of objects, single object, or NDJSON) into a table.
 * Throws on malformed input with a readable message.
 */
export function parseJson(text: string): JsonTable {
  const src = text.replace(/^\uFEFF/, '').trim();
  if (src === '') return { header: [], rows: [], flattened: false };

  let records: Record<string, unknown>[] | null = null;

  try {
    const parsed: unknown = JSON.parse(src);
    if (isRecordArray(parsed)) records = parsed;
    else if (Array.isArray(parsed)) {
      records = parsed.map((v) =>
        typeof v === 'object' && v !== null && !Array.isArray(v)
          ? (v as Record<string, unknown>)
          : { value: v },
      );
    } else if (typeof parsed === 'object' && parsed !== null) {
      records = [parsed as Record<string, unknown>];
    }
  } catch {
    // Fall back to NDJSON: one record per line.
    const lines = src.split(/\r?\n/).filter((l) => l.trim() !== '');
    const collected: Record<string, unknown>[] = [];
    let ok = lines.length > 0;
    for (const line of lines) {
      try {
        const parsedLine: unknown = JSON.parse(line);
        if (typeof parsedLine === 'object' && parsedLine !== null && !Array.isArray(parsedLine)) {
          collected.push(parsedLine as Record<string, unknown>);
        } else {
          collected.push({ value: parsedLine });
        }
      } catch {
        ok = false;
        break;
      }
    }
    if (ok) records = collected;
  }

  if (!records) {
    throw new Error(
      'Not valid JSON and not valid NDJSON. Expected an array of objects, a single object, or one JSON object per line.',
    );
  }

  const header: string[] = [];
  const seen = new Set<string>();
  let flattened = false;
  for (const rec of records) {
    const flat = flattenRecord(rec);
    if (Object.keys(flat).some((k) => k.includes('.'))) flattened = true;
    for (const k of Object.keys(flat)) {
      if (!seen.has(k)) {
        seen.add(k);
        header.push(k);
      }
    }
  }

  const rows = records.map((rec) => {
    const flat = flattenRecord(rec);
    return header.map((k) => (k in flat ? flat[k] : ''));
  });

  return { header, rows, flattened };
}
